/**
 * Electronic-signature client logic (PROJECT_SPEC.md §5.6; docs/pages/03-signature/signature-dialog.md;
 * 21 CFR 11.50, 11.100, 11.200(a); draft Annex 11 §13).
 *
 * A signature is a step-up re-authentication at the identity provider followed by one API call carrying the
 * fresh id token. The browser leaves the application for the provider, so what the person was doing has to
 * survive the round trip. That is the "pending intent": which record, which meaning, which hash, and where to
 * come back to. It holds NO credential and no token (ESIG-008) -- the password is typed on the provider's page,
 * this application never sees it -- and it is read exactly once, so a replayed callback finds nothing.
 */
import { type SignatureMeaning, subjectStateHash } from "@corpex-ebr/domain/signature/binding";

/** The authentication class the server demands of a signature (OIDC_SIGNATURE_ACR; spec §5.6 "silver"). */
export const SIGNATURE_ACR = (import.meta.env["VITE_OIDC_SIGNATURE_ACR"] as string | undefined) ?? "silver";

const INTENT_KEY = "ebr.signing.intent";
const OUTCOME_KEY = "ebr.signing.outcome";
/** A step-up that takes longer than this was abandoned; the server's own limit (120 s) is far shorter. */
export const INTENT_TTL_MS = 10 * 60 * 1000;

/** Everything needed to finish a signature after the redirect. Deliberately free of any secret. */
export interface SigningIntent {
  batchId: string;
  subjectType: string;
  subjectId: string;
  meaning: SignatureMeaning;
  payloadHash: string;
  /** Path (relative to the router base) to return to, so the person does not lose their place. */
  returnPath: string;
  createdAt: number;
}

/** What happened when the signature was attempted, shown once on the screen the person returns to. */
export interface SigningOutcome {
  ok: boolean;
  subjectId: string;
  meaning: string;
  message: string;
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function saveIntent(store: Store, intent: SigningIntent): void {
  store.setItem(INTENT_KEY, JSON.stringify(intent));
}

/** True while an intent is waiting; does not consume it. */
export function hasIntent(store: Store): boolean {
  return store.getItem(INTENT_KEY) !== null;
}

function isIntent(x: unknown): x is SigningIntent {
  const i = x as Partial<SigningIntent> | null;
  return (
    !!i &&
    typeof i.batchId === "string" &&
    typeof i.subjectType === "string" &&
    typeof i.subjectId === "string" &&
    typeof i.meaning === "string" &&
    typeof i.payloadHash === "string" &&
    typeof i.returnPath === "string" &&
    typeof i.createdAt === "number"
  );
}

/** Single use: the intent is removed as it is read, valid or not, so it can never be applied twice. */
export function takeIntent(store: Store, now: number = Date.now()): SigningIntent | null {
  const raw = store.getItem(INTENT_KEY);
  store.removeItem(INTENT_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isIntent(parsed)) return null;
    return now - parsed.createdAt > INTENT_TTL_MS ? null : parsed;
  } catch {
    return null;
  }
}

export function saveOutcome(store: Store, outcome: SigningOutcome): void {
  store.setItem(OUTCOME_KEY, JSON.stringify(outcome));
}

/** Read once by the screen the person returns to. */
export function takeOutcome(store: Store): SigningOutcome | null {
  const raw = store.getItem(OUTCOME_KEY);
  store.removeItem(OUTCOME_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SigningOutcome;
  } catch {
    return null;
  }
}

/** First 12 hex of the content hash, as the dialog previews it. */
export const hashPreview = (hash: string): string => hash.slice(0, 12);

/** sha256(JCS(state)): the same function the server re-runs, so a mismatch means the record changed (ESIG-016). */
export const payloadHashOf = (state: unknown): Promise<string> => subjectStateHash(state as never);

/**
 * The nonce the provider must echo in the id token. It binds the re-authentication to THIS request
 * (ESIG-034); the server derives the same value and refuses anything else. Mirrors signingNonce() in the API.
 */
export function nonceFor(r: Pick<SigningIntent, "subjectType" | "subjectId" | "meaning" | "payloadHash">) {
  return subjectStateHash({ n: [r.subjectType, r.subjectId, r.meaning, r.payloadHash] });
}

/** The permission that lets a person sign as each meaning (signature_authority, migration 0006). */
export const PERMISSION_FOR_MEANING: Partial<Record<string, readonly string[]>> = {
  AUTHORED: ["mbr.author"],
  CHECKED: ["mbr.check"],
  PERFORMED: ["batch.step.perform", "control.override", "control.operate"],
  VERIFIED: ["batch.step.verify"],
  WITNESSED: ["batch.step.verify"],
  APPROVED: ["batch.release", "mbr.approve", "control.release.approve"],
  CERTIFIED_QP: ["batch.certify_qp"],
};

/** Order in which a step's meanings are applied; a later one needs the earlier (ESIG-020/021/022). */
const LADDER: readonly string[] = ["PERFORMED", "VERIFIED", "APPROVED"];

export interface SignedMeaning {
  meaning: string;
}

/**
 * The meanings this person may sign right now: owed, not yet applied, next on the ladder, and within their
 * authority. Advisory only -- the server decides -- but it keeps the dialog from offering what will be refused.
 */
export function signableMeanings(
  required: readonly string[],
  signed: readonly SignedMeaning[],
  permissions: readonly string[],
): string[] {
  const done = new Set(signed.map((s) => s.meaning));
  return required.filter((m) => {
    if (done.has(m)) return false;
    const rung = LADDER.indexOf(m);
    const earlier = rung > 0 ? LADDER.slice(0, rung).filter((e) => required.includes(e)) : [];
    if (earlier.some((e) => !done.has(e))) return false;
    const need = PERMISSION_FOR_MEANING[m];
    return !need || need.some((p) => permissions.includes(p));
  });
}

/** A distinct message per refusal; none names another user (ESIG-006). Keys are server codes (signature.service.ts). */
const REFUSALS: Record<string, string> = {
  SIGNER_MISMATCH:
    "The person who re-authenticated is not the person signed in here. Nothing was signed. Sign in as yourself and try again.",
  AUTH_TOO_OLD:
    "Your re-authentication expired before the signature was applied. Nothing was signed. Press Sign again.",
  TOKEN_EXPIRED:
    "Your re-authentication expired before the signature was applied. Nothing was signed. Press Sign again.",
  ACR_TOO_LOW: "The sign-in did not reach the authentication strength a signature needs. Nothing was signed.",
  NONCE_MISMATCH: "That re-authentication was not for this signature. Nothing was signed. Press Sign again.",
  JTI_REPLAYED: "That re-authentication was already used. Nothing was signed. Press Sign again.",
  TOKEN_INVALID: "The re-authentication could not be verified. Nothing was signed.",
  IDP_UNAVAILABLE: "The identity provider is unavailable, so signing is refused. Nothing was signed.",
  SIGNING_LOCKED: "Signing is temporarily locked after repeated failed attempts. Contact your administrator.",
  ALREADY_SIGNED: "You have already signed this record with this meaning.",
  SIGNER_IS_PERFORMER:
    "A different person must sign this: the verifier cannot be the person who performed or signed the step.",
  SEQUENCE: "An earlier signature is needed first (for example, PERFORMED before VERIFIED).",
  NOT_AUTHORISED: "You are not authorised to sign this record with this meaning.",
  STATE_NOT_SIGNABLE: "This record is not in a state that can be signed.",
  FIELDS_MISSING: "Required fields are blank, so the record cannot be signed yet.",
  HASH_MISMATCH: "The record changed after you reviewed it. Reload it and review the current version.",
  TRAINING_EXPIRED: "Your training for this record is not current, so you cannot sign it.",
};

/** The sentence shown for a refusal: ours by code where we have one, the server's own words otherwise. */
export function signingRefusal(code: string | undefined, serverMessage?: string): string {
  return (
    (code ? REFUSALS[code] : undefined) ??
    serverMessage ??
    "The signature could not be applied. Nothing was signed."
  );
}

/** The provider sends `error=` back when the person backs out or the login fails; a cancel is not a failure. */
export function stepUpErrorMessage(message: string): string {
  return /cancel|access_denied|login_required/i.test(message)
    ? "Signing was cancelled. Nothing was signed."
    : `${message}`;
}

// ------------------------------------------------------------------ dialog state machine

export type DialogPhase = "review" | "redirecting" | "failed";

export interface DialogState {
  phase: DialogPhase;
  meaning: string | null;
  error: string | null;
}

export type DialogEvent =
  | { type: "choose"; meaning: string }
  | { type: "submit" }
  | { type: "redirect-failed"; message: string };

export const initialDialog = (only: string | null): DialogState => ({
  phase: "review",
  meaning: only,
  error: null,
});

/**
 * The meaning to preselect: only when exactly one is allowed. Several allowed means the person chooses; the
 * product never guesses between them (ESIG-013). One allowed meaning needed an extra click for nothing (#30).
 */
export function soleMeaning(offered: readonly string[]): string | null {
  return offered.length === 1 ? (offered[0] ?? null) : null;
}

/** Pure, so the transitions are testable without a browser. A signature cannot start without a chosen meaning. */
export function dialogReducer(s: DialogState, e: DialogEvent): DialogState {
  switch (e.type) {
    case "choose":
      return s.phase === "redirecting" ? s : { phase: "review", meaning: e.meaning, error: null };
    case "submit":
      return s.phase !== "redirecting" && s.meaning ? { ...s, phase: "redirecting", error: null } : s;
    case "redirect-failed":
      return { ...s, phase: "failed", error: e.message };
  }
}

/** Why the Sign button is disabled, in words, or null when it may be pressed. */
export function signBlocker(args: {
  state: DialogState;
  signable: boolean;
  notSignableReason: string | null;
  missingFields: readonly string[];
  online: boolean;
}): string | null {
  if (!args.online) return "Signatures require a connection.";
  if (!args.signable) return args.notSignableReason ?? "This record cannot be signed now.";
  if (args.missingFields.length) return `Required fields are blank: ${args.missingFields.join(", ")}.`;
  if (!args.state.meaning) return "Choose the meaning of your signature first.";
  if (args.state.phase === "redirecting") return "Signing…";
  return null;
}

/** Per step: how many of the signatures it owes have been applied (for the spine and the overview). */
export function signatureTally(
  rows: readonly { mbr_step_id: string | null; signature_id: string | null }[],
): Map<string, { signed: number; total: number }> {
  const out = new Map<string, { signed: number; total: number }>();
  for (const r of rows) {
    if (!r.mbr_step_id) continue;
    const t = out.get(r.mbr_step_id) ?? { signed: 0, total: 0 };
    out.set(r.mbr_step_id, { signed: t.signed + (r.signature_id ? 1 : 0), total: t.total + 1 });
  }
  return out;
}

/** The role to print for the meaning being signed: the one that authorises it, else the server's default (#38). */
export function roleFor(
  signer: { role: string | null; roleByMeaning?: Record<string, string | null> },
  meaning: string | null,
): string | null {
  return meaning !== null && signer.roleByMeaning && meaning in signer.roleByMeaning
    ? (signer.roleByMeaning[meaning] ?? null)
    : signer.role;
}
